import React, { useLayoutEffect, useRef, useState } from "react";
import {
  TextField,
  TextFieldProps,
} from "@mui/material";
import { alpha, Theme } from "@mui/material/styles";
import { copyTextToClipboard } from "../../utils/copyTextToClipboard";

/** Shared by ReadOnlyTextField and read-only Autocomplete wrappers (underline/cursor/hover). */
export const readOnlyFieldPresentationSx = {
  cursor: "default",
  "& .MuiInputBase-root": { cursor: "default" },
  "& .MuiInputBase-input, & .MuiInputBase-inputMultiline": {
    cursor: "default",
  },
  "& .MuiInput-underline:after": { transform: "scaleX(0)" },
  "& .MuiInput-underline.Mui-focused:after": { transform: "scaleX(0)" },
  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
    borderBottomWidth: "1px",
  },
  "& .MuiInput-root:hover:not(.Mui-disabled) .MuiInput-underline:before": {
    borderBottomWidth: "1px",
  },
  "& .MuiFilledInput-underline:after": { transform: "scaleX(0)" },
  "& .MuiFilledInput-root.Mui-focused .MuiFilledInput-underline:after": {
    transform: "scaleX(0)",
  },
  "& .MuiFilledInput-root:hover:not(.Mui-disabled):before": {
    borderBottomWidth: "1px",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
  },
};

const multilineOutlinedHoverSx = (theme: Theme) => ({
  "& .MuiOutlinedInput-root:hover:not(.Mui-disabled):not(.Mui-focused) .MuiOutlinedInput-notchedOutline":
    {
      borderColor: alpha(theme.palette.text.primary, 0.23),
    },
  "& .MuiOutlinedInput-root.Mui-error:hover:not(.Mui-disabled):not(.Mui-focused) .MuiOutlinedInput-notchedOutline":
    {
      borderColor: theme.palette.error.main,
    },
});

export interface ReadOnlyTextFieldProps {
  label?: string;
  value: string | number;
  multiline?: boolean;
  variant?: TextFieldProps["variant"];
  fullWidth?: boolean;
  size?: TextFieldProps["size"];
  sx?: TextFieldProps["sx"];
  inputRef?: TextFieldProps["inputRef"];
  error?: boolean;
  helperText?: TextFieldProps["helperText"];
  canCopy?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
}

const ReadOnlyTextField: React.FC<ReadOnlyTextFieldProps> = ({
  canCopy,
  label,
  value,
  multiline,
  variant,
  fullWidth,
  size,
  sx,
  inputRef,
  error,
  helperText,
  startAdornment,
  endAdornment,
}) => {
  const inputElementRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(
    null
  );
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [textareaMounted, setTextareaMounted] = useState(false);
  const didSetInitialCursorRef = useRef(false);
  const inputRefRef = useRef(inputRef);
  inputRefRef.current = inputRef;

  const setRefs = useRef((el: HTMLInputElement | HTMLTextAreaElement | null) => {
    inputElementRef.current = el;
    textareaRef.current = multiline ? (el as HTMLTextAreaElement | null) : null;
    if (multiline && el) setTextareaMounted(true);
    const ir = inputRefRef.current;
    if (typeof ir === "function") ir(el);
    else if (ir)
      (
        ir as React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>
      ).current = el;
  });

  useLayoutEffect(() => {
    if (didSetInitialCursorRef.current) return;
    const el = inputElementRef.current;
    if (!el) return;
    didSetInitialCursorRef.current = true;

    const isTextArea = el instanceof HTMLTextAreaElement;
    const isTextLikeInput =
      el instanceof HTMLInputElement &&
      !["number", "date", "time", "datetime-local", "month", "week"].includes(
        el.type
      );

    if (isTextArea || isTextLikeInput) {
      el.setSelectionRange?.(0, 0);
    }
    el.scrollLeft = 0;
    if ("scrollTop" in el) el.scrollTop = 0;
  }, [multiline]);

  useLayoutEffect(() => {
    if (!multiline) return;
    const textarea = textareaRef.current;
    if (!textarea) return;
    const handleWheel = (e: WheelEvent) => {
      e.stopPropagation();
      e.preventDefault();
      textarea.scrollTop += e.deltaY;
    };
    textarea.addEventListener("wheel", handleWheel, { passive: false });
    return () => textarea.removeEventListener("wheel", handleWheel);
  }, [multiline, textareaMounted]);

  const text = String(value);
  const copyEnabled = Boolean(canCopy) && text.length > 0;

  return (
    <TextField
      label={label}
      value={text}
      type="text"
      multiline={multiline}
      variant={variant}
      fullWidth={fullWidth}
      size={size}
      title={copyEnabled ? "Click to copy" : undefined}
      sx={[
        readOnlyFieldPresentationSx,
        ...(copyEnabled
          ? [
              {
                cursor: "pointer",
                "& .MuiInputBase-root": { cursor: "pointer" },
                "& .MuiInputBase-input, & .MuiInputBase-inputMultiline": {
                  cursor: "pointer",
                },
              },
            ]
          : []),
        ...(multiline ? [multilineOutlinedHoverSx] : []),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      inputRef={setRefs.current!}
      error={error}
      helperText={helperText}
      onClick={
        copyEnabled
          ? (e) => {
              e.preventDefault();
              e.stopPropagation();
              void copyTextToClipboard(text);
            }
          : undefined
      }
      slotProps={{
        input: {
          readOnly: true,
          startAdornment,
          endAdornment,
          ...(copyEnabled
            ? { "aria-label": "Click to copy" }
            : {}),
        },
      }}
    />
  );
};

export default ReadOnlyTextField;
